/**
 * @file 无限滚动虚拟列表
 * @author Yangholmes 2024-03-14
 */

import { ReactNode, useRef, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import cx from 'classnames';
import { useVirtualizer } from '@tanstack/react-virtual';
import Icon from '@/components/CommonIcon';
import Styles from './mIndex.module.scss';

export const MEASURE_SIZE_CACHE = 'MEASURE_SIZE_CACHE';

export interface IvsListOptions<T> {
  channelSlug?: string;
  className?: string;
  /** 初始化数据 */
  initData?: Array<T>;
  /** 预估大小 */
  estimateSize?: number;
  /** 超载数量 */
  overscan?: number;
  /** 初始分页位置 */
  initialOffset?: number;
  /** 初始是否存在下一页 */
  initialHasNextPage?: boolean;
  /** 列表初始滚动位置 */
  initialPageScrollTop?: number;
  /** 滚动事件回调 */
  onScroll?: (offset?: number) => void;
  /** 组件销毁事件 */
  onDestory?: (offset?: number) => void;
  /** 加载中 组件 */
  renderBottomItem?: (
    isLoading: boolean,
    hasNextPage: boolean,
  ) => ReactNode;
  /** 元素 组件 */
  renderItem: (item: T, index: number) => ReactNode;
  /** 加载数据函数 */
  queryFn: (
    offset?: number,
    size?: number
  ) => Promise<{
    data: Array<T>;
    nextOffset: number;
    hasNextPage: boolean;
  }>;
}

export function useIvsList<T extends { height?: number }>(
  options: IvsListOptions<T>
) {
  const {
    channelSlug,
    className,
    initData = [],
    estimateSize = 16,
    overscan = 6,
    initialOffset = 0,
    initialHasNextPage = true,
    initialPageScrollTop = 0,
    renderBottomItem,
    renderItem,
    onScroll,
    onDestory,
    queryFn,
  } = options;

  const t = useTranslations();
  const { pathname } = useRouter();

  const MEASURE_SIZE_CACHE_KEY = `${MEASURE_SIZE_CACHE}${pathname}${channelSlug}`;

  const measureSizeCache = typeof window !== 'undefined' ? window?.[MEASURE_SIZE_CACHE_KEY] : undefined;

  const parentRef = useRef<HTMLDivElement>(null);
  const isLocked = useRef<boolean>(false);
  const isMount = useRef<boolean>(false);

  const [data, setData] = useState<T[]>([...initData]);
  const [offest, setOffest] = useState<number>(initialOffset);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);

  const virtualizer = useVirtualizer({
    initialOffset: initialPageScrollTop,
    count: data.length + 1,
    getScrollElement: () => document.querySelector('#__next'),
    estimateSize: (i) => {
      return measureSizeCache?.[i]?.size || estimateSize;
    },
    overscan,
    onChange(inst) {
      const { isScrolling, scrollOffset } = inst;
      if (isScrolling) {
        onScroll?.(scrollOffset);
      }
    },
    initialMeasurementsCache: measureSizeCache,
  });

  const items = virtualizer.getVirtualItems();

  const getData = () => {
    return data.slice();
  };

  const getOffset = () => {
    return virtualizer.scrollOffset;
  };

  const setOffset = (top: number = 0) => {
    virtualizer.scrollToOffset(top);
  };

  const fetchNextPage = (initOffest?: number) => {
    const _offset = offest || initOffest;
    setIsLoading(true);
    return queryFn(_offset)
      .then((res) => {
        const { data: rawData, nextOffset, hasNextPage = false } = res;
        const _data = [...data, ...rawData];
        setOffest(nextOffset);
        setData(_data);
        setHasNextPage(hasNextPage);
        setIsLoading(false);
        isLocked.current = false;
      })
      .catch(() => {
        setIsLoading(false);
      })
  };

  const fetchFirstPage = (initOffset: number = 0) => {
    setOffest(initOffset);
    return fetchNextPage(initOffset);
  };

  useEffect(() => {
    window[MEASURE_SIZE_CACHE_KEY] = virtualizer.measurementsCache;
  }, [virtualizer.measurementsCache]);

  useEffect(() => {
    const [lastItem] = [...items].reverse();
    if (
      isMount &&
      !isLocked?.current &&
      lastItem &&
      lastItem.index >= data.length &&
      hasNextPage &&
      typeof offest !== undefined
    ) {
      isLocked.current = true;
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hasNextPage]);

  useEffect(() => {
    isMount.current = true;
    return () => {
      isMount.current = false;
      onDestory && onDestory(getOffset());
    }
  }, []);

  const renderBottom = () => {
    if (renderBottomItem) {
      return renderBottomItem(isLoading, hasNextPage);
    } else {
      /** 默认底部元素的渲染 */
      if (!isLoading && !hasNextPage) {
        return <div className={Styles.bottom}>{t('no_more_data')}</div>;
      }
      if (isLoading) {
        return (
          <div className={Styles.bottom}>
            <Icon icon='loading-white' className={Styles.loading} />
          </div>
        );
      }
    }
  };

  const listSegment = (
    <div ref={parentRef} className={cx(Styles.root, className)}>
      <div
        className={Styles.wrapper}
        style={{
          height: virtualizer.getTotalSize(),
        }}
      >
        <div
          className={Styles.list}
          style={{
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => {
            const { index, key } = virtualRow;
            const isBottom = index >= data.length;

            return (
              <div
                key={key}
                data-index={index}
                ref={virtualizer.measureElement}
              >
                {isBottom
                  ? renderBottom()
                  : renderItem(data[index], index)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return {
    /** 列表组件 */
    listSegment,
    /** 获取当前数据 */
    getData,
    /** 获取当前滚动值 */
    getOffset,
    /** 滚动到指定位置 */
    setOffset,
    /** 首次加载函数 */
    fetchFirstPage,
  };
}
